import React, { useState, useEffect } from 'react';
import { X, Plus, Edit2, Trash2, ChevronUp, ChevronDown, Save, XCircle } from 'lucide-react';
import { positionApi, Position } from '../services/positionApi';
import { useAlert } from '../contexts/AlertContext';

interface PositionManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPositionsUpdated?: () => void;
}

const PositionManagementModal: React.FC<PositionManagementModalProps> = ({
  isOpen,
  onClose,
  onPositionsUpdated
}) => {
  const { showConfirm } = useAlert();
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState<string>('');
  const [editingUnitCost, setEditingUnitCost] = useState<number>(0);
  const [newPositionName, setNewPositionName] = useState<string>('');
  const [newPositionUnitCost, setNewPositionUnitCost] = useState<number>(0);
  const [isAdding, setIsAdding] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchPositions();
    }
  }, [isOpen, showInactive]);

  const fetchPositions = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await positionApi.getAll(showInactive);
      setPositions(data);
    } catch (err: any) {
      setError(err.message || '직급 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newPositionName.trim()) {
      setError('직급명을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await positionApi.create({
        name: newPositionName.trim(),
        unitCost: newPositionUnitCost || 0
      });
      setNewPositionName('');
      setNewPositionUnitCost(0);
      setIsAdding(false);
      await fetchPositions();
      onPositionsUpdated?.();
    } catch (err: any) {
      setError(err.message || '직급 추가에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (id: number) => {
    if (!editingName.trim()) {
      setError('직급명을 입력해주세요.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await positionApi.update(id, {
        name: editingName.trim(),
        unitCost: editingUnitCost || 0
      });
      setEditingId(null);
      setEditingName('');
      setEditingUnitCost(0);
      await fetchPositions();
      onPositionsUpdated?.();
    } catch (err: any) {
      setError(err.message || '직급 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (position: Position) => {
    const confirmed = await showConfirm(`"${position.name}" 직급을 삭제하시겠습니까?`);
    if (!confirmed) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await positionApi.delete(position.id);
      await fetchPositions();
      onPositionsUpdated?.();
    } catch (err: any) {
      // 사용 중인 직원이 있으면 에러 메시지 표시
      setError(err.message || '직급 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleReorder = async (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= positions.length) return;

    const newPositions = [...positions];
    [newPositions[index], newPositions[newIndex]] = [newPositions[newIndex], newPositions[index]];

    setLoading(true);
    setError('');
    try {
      await positionApi.reorder(newPositions.map(p => p.id));
      setPositions(newPositions);
      onPositionsUpdated?.();
    } catch (err: any) {
      setError(err.message || '순서 변경에 실패했습니다.');
      await fetchPositions(); // Revert on error
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (position: Position) => {
    setLoading(true);
    setError('');
    try {
      await positionApi.update(position.id, { isActive: !position.isActive });
      await fetchPositions();
      onPositionsUpdated?.();
    } catch (err: any) {
      setError(err.message || '상태 변경에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (position: Position) => {
    setEditingId(position.id);
    setEditingName(position.name);
    setEditingUnitCost(position.unitCost || 0);
    setIsAdding(false);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName('');
    setEditingUnitCost(0);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">직급 관리</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center text-sm text-gray-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="mr-2"
            />
            비활성화 직급 포함
          </label>

          {!isAdding && (
            <button
              onClick={() => {
                setIsAdding(true);
                cancelEditing();
              }}
              className="flex items-center px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              disabled={loading}
            >
              <Plus size={16} className="mr-1" />
              새 직급
            </button>
          )}
        </div>

        {isAdding && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newPositionName}
                onChange={(e) => setNewPositionName(e.target.value)}
                placeholder="직급명"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsAdding(false);
                    setNewPositionName('');
                    setNewPositionUnitCost(0);
                  }
                }}
              />
              <input
                type="number"
                value={newPositionUnitCost || ''}
                onChange={(e) => setNewPositionUnitCost(Number(e.target.value) || 0)}
                placeholder="직급원가(만원)"
                className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAdd();
                  if (e.key === 'Escape') {
                    setIsAdding(false);
                    setNewPositionName('');
                    setNewPositionUnitCost(0);
                  }
                }}
              />
              <button
                onClick={handleAdd}
                className="p-2 text-green-600 hover:bg-green-100 rounded-lg"
                disabled={loading}
              >
                <Save size={20} />
              </button>
              <button
                onClick={() => {
                  setIsAdding(false);
                  setNewPositionName('');
                  setNewPositionUnitCost(0);
                }}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <XCircle size={20} />
              </button>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading && positions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">로딩 중...</div>
          ) : positions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">등록된 직급이 없습니다.</div>
          ) : (
            <div className="space-y-2">
              {positions.map((position, index) => (
                <div
                  key={position.id}
                  className={`flex items-center gap-2 p-3 rounded-lg border ${
                    position.isActive
                      ? 'bg-white border-gray-200'
                      : 'bg-gray-100 border-gray-300 opacity-60'
                  }`}
                >
                  <div className="flex flex-col">
                    <button
                      onClick={() => handleReorder(index, 'up')}
                      disabled={index === 0 || loading}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronUp size={16} />
                    </button>
                    <button
                      onClick={() => handleReorder(index, 'down')}
                      disabled={index === positions.length - 1 || loading}
                      className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <ChevronDown size={16} />
                    </button>
                  </div>

                  <div className="flex-1">
                    {editingId === position.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 px-2 py-1 border border-blue-400 rounded focus:ring-2 focus:ring-blue-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') cancelEditing();
                          }}
                        />
                        <input
                          type="number"
                          value={editingUnitCost || ''}
                          onChange={(e) => setEditingUnitCost(Number(e.target.value) || 0)}
                          placeholder="직급원가"
                          className="w-28 px-2 py-1 border border-blue-400 rounded focus:ring-2 focus:ring-blue-500 text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleEdit(position.id);
                            if (e.key === 'Escape') cancelEditing();
                          }}
                        />
                        <span className="text-xs text-gray-500">만원</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span className={`font-medium ${!position.isActive ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                          {position.name}
                        </span>
                        <span className="text-sm text-indigo-600 font-semibold">
                          {position.unitCost ? `${position.unitCost.toLocaleString()}만원` : '-'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-1">
                    {editingId === position.id ? (
                      <>
                        <button
                          onClick={() => handleEdit(position.id)}
                          className="p-1.5 text-green-600 hover:bg-green-100 rounded"
                          disabled={loading}
                        >
                          <Save size={16} />
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                        >
                          <XCircle size={16} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(position)}
                          className="p-1.5 text-blue-600 hover:bg-blue-100 rounded"
                          disabled={loading}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleToggleActive(position)}
                          className={`px-2 py-1 text-xs rounded ${
                            position.isActive
                              ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200'
                              : 'text-green-700 bg-green-100 hover:bg-green-200'
                          }`}
                          disabled={loading}
                        >
                          {position.isActive ? '비활성화' : '활성화'}
                        </button>
                        <button
                          onClick={() => handleDelete(position)}
                          className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                          disabled={loading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default PositionManagementModal;
